import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ClipboardList, Plus, BarChart3, Star, Send, Trash2, Lock, Users } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function SurveysPage() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showResults, setShowResults] = useState(null);
  const [showRespond, setShowRespond] = useState(null);
  const [results, setResults] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', anonymous: true, questions: [{ text: '', type: 'rating', options: [] }] });
  const [answers, setAnswers] = useState({});

  const fetchData = async () => {
    try { const res = await api.get('/surveys'); setSurveys(res.data); } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const addQuestion = () => setForm(f => ({ ...f, questions: [...f.questions, { text: '', type: 'rating', options: [] }] }));
  const updateQ = (i, field, val) => { const qs = [...form.questions]; qs[i] = { ...qs[i], [field]: val }; setForm(f => ({ ...f, questions: qs })); };
  const removeQ = (i) => setForm(f => ({ ...f, questions: f.questions.filter((_, idx) => idx !== i) }));

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!form.questions.some(q => q.text.trim())) { toast.error('At least one question required'); return; }
    try { await api.post('/surveys', form); toast.success('Survey created'); setShowCreate(false); setForm({ title: '', description: '', anonymous: true, questions: [{ text: '', type: 'rating' }] }); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRespond = async (surveyId) => {
    try { await api.post(`/surveys/${surveyId}/respond`, { answers }); toast.success('Response submitted'); setShowRespond(null); setAnswers({}); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const viewResults = async (surveyId) => {
    try { const res = await api.get(`/surveys/${surveyId}/results`); setResults(res.data); setShowResults(surveyId); } catch { toast.error('Failed to load results'); }
  };

  const closeSurvey = async (id) => {
    try { await api.put(`/surveys/${id}/close`); toast.success('Survey closed'); fetchData(); } catch { toast.error('Failed'); }
  };

  const deleteSurvey = async (id) => {
    if (!window.confirm('Delete this survey and all responses?')) return;
    try { await api.delete(`/surveys/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="surveys-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg shadow-pink-500/20">
              <ClipboardList size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight dark:text-white">Employee Surveys</h1>
              <p className="text-sm text-muted-foreground">{surveys.length} survey{surveys.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-survey-btn"><Plus size={14} className="mr-1.5" />New Survey</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? <div className="col-span-full text-center py-12 text-muted-foreground">Loading...</div> :
           surveys.length === 0 ? (
            <div className="col-span-full"><Card><CardContent className="py-12 text-center"><ClipboardList size={32} className="mx-auto text-muted-foreground mb-2" /><p className="text-sm text-muted-foreground">No surveys yet.</p></CardContent></Card></div>
           ) : surveys.map(s => (
            <Card key={s.id} data-testid={`survey-card-${s.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">{s.title}</CardTitle>
                  <Badge variant={s.status === 'active' ? 'default' : 'secondary'}>{s.status}</Badge>
                </div>
                {s.description && <p className="text-xs text-muted-foreground mt-1">{s.description}</p>}
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                  <span>{s.questions?.length || 0} questions</span>
                  <span>{s.response_count || 0} responses</span>
                  {s.anonymous && <Badge variant="outline" className="text-[9px]"><Lock size={8} className="mr-0.5" />Anonymous</Badge>}
                </div>
                <div className="flex gap-2">
                  {s.status === 'active' && <Button size="sm" variant="outline" onClick={() => { setShowRespond(s); setAnswers({}); }} data-testid={`respond-btn-${s.id}`}><Send size={12} className="mr-1" />Respond</Button>}
                  <Button size="sm" variant="outline" onClick={() => viewResults(s.id)} data-testid={`results-btn-${s.id}`}><BarChart3 size={12} className="mr-1" />Results</Button>
                  {s.status === 'active' && <Button size="sm" variant="ghost" className="text-warning" onClick={() => closeSurvey(s.id)}><Lock size={12} /></Button>}
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteSurvey(s.id)}><Trash2 size={12} /></Button>
                </div>
              </CardContent>
            </Card>
           ))}
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto" data-testid="create-survey-dialog">
            <DialogHeader><DialogTitle>Create Survey</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} data-testid="survey-title-input" className="mt-1" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="mt-1" /></div>
              <Label>Questions</Label>
              {form.questions.map((q, i) => (
                <div key={i} className="p-3 border rounded-lg space-y-2">
                  <div className="flex gap-2">
                    <Input value={q.text} onChange={(e) => updateQ(i, 'text', e.target.value)} placeholder={`Question ${i + 1}`} className="flex-1" data-testid={`question-input-${i}`} />
                    <Select value={q.type} onValueChange={(v) => updateQ(i, 'type', v)}>
                      <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rating">Rating (1-5)</SelectItem>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="choice">Choice</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.questions.length > 1 && <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeQ(i)}><Trash2 size={14} /></Button>}
                  </div>
                  {q.type === 'choice' && (
                    <Input value={(q.options || []).join(', ')} onChange={(e) => updateQ(i, 'options', e.target.value.split(',').map(o => o.trim()))} placeholder="Option 1, Option 2, Option 3" className="text-xs" />
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addQuestion} className="w-full border-dashed"><Plus size={14} className="mr-1" />Add Question</Button>
              <Button onClick={handleCreate} className="w-full" data-testid="create-survey-submit-btn">Create Survey</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Respond Dialog */}
        <Dialog open={!!showRespond} onOpenChange={() => setShowRespond(null)}>
          <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto" data-testid="respond-survey-dialog">
            <DialogHeader><DialogTitle>{showRespond?.title}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {(showRespond?.questions || []).map((q, i) => (
                <div key={q.id} className="space-y-2">
                  <Label className="text-sm">{i + 1}. {q.text}</Label>
                  {q.type === 'rating' ? (
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(n => (
                        <button key={n} onClick={() => setAnswers(a => ({ ...a, [q.id]: n }))}
                          className={`w-10 h-10 rounded-lg border-2 text-sm font-bold transition-all ${answers[q.id] === n ? 'border-amber-400 bg-warning/10 text-warning' : 'border-border text-muted-foreground'}`}
                          data-testid={`rating-${q.id}-${n}`}>{n}</button>
                      ))}
                    </div>
                  ) : q.type === 'choice' ? (
                    <div className="flex flex-wrap gap-2">
                      {(q.options || []).map(opt => (
                        <button key={opt} onClick={() => setAnswers(a => ({ ...a, [q.id]: opt }))}
                          className={`px-3 py-1.5 rounded-lg border text-xs ${answers[q.id] === opt ? 'border-blue-400 bg-info/10 text-info' : 'border-border'}`}>{opt}</button>
                      ))}
                    </div>
                  ) : (
                    <Textarea value={answers[q.id] || ''} onChange={(e) => setAnswers(a => ({ ...a, [q.id]: e.target.value }))} rows={2} />
                  )}
                </div>
              ))}
              <Button onClick={() => handleRespond(showRespond?.id)} className="w-full" data-testid="submit-response-btn"><Send size={14} className="mr-1.5" />Submit Response</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Results Dialog */}
        <Dialog open={!!showResults} onOpenChange={() => setShowResults(null)}>
          <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto" data-testid="survey-results-dialog">
            <DialogHeader><DialogTitle>Survey Results</DialogTitle></DialogHeader>
            {results && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-3 bg-muted/50 dark:bg-muted rounded-xl">
                  <div><span className="text-xs text-muted-foreground">Responses</span><p className="text-lg font-bold dark:text-white">{results.response_count}</p></div>
                  <div><span className="text-xs text-muted-foreground">Overall Satisfaction</span><p className="text-lg font-bold text-warning">{results.overall_satisfaction}/5</p></div>
                </div>
                {Object.entries(results.questions || {}).map(([qid, qr]) => (
                  <div key={qid} className="p-3 border rounded-xl">
                    <p className="text-sm font-medium dark:text-white mb-2">{qr.text}</p>
                    {qr.type === 'rating' ? (
                      <div>
                        <div className="flex items-center gap-2 mb-1"><Star size={14} className="text-amber-400" /><span className="text-lg font-bold text-warning">{qr.average}</span><span className="text-xs text-muted-foreground">/ 5 ({qr.count} responses)</span></div>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map(n => (
                            <div key={n} className="flex-1">
                              <div className="h-8 bg-muted dark:bg-muted rounded relative overflow-hidden">
                                <div className="absolute bottom-0 w-full bg-amber-400 rounded-b transition-all" style={{ height: `${qr.count > 0 ? ((qr.distribution?.[String(n)] || 0) / qr.count) * 100 : 0}%` }} />
                              </div>
                              <p className="text-[10px] text-center text-muted-foreground mt-0.5">{n}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : qr.type === 'choice' ? (
                      <div className="space-y-1">{Object.entries(qr.distribution || {}).map(([opt, cnt]) => (
                        <div key={opt} className="flex items-center gap-2"><span className="text-xs w-24 truncate">{opt}</span><div className="flex-1 h-4 bg-muted dark:bg-muted rounded-full"><div className="h-full bg-blue-400 rounded-full" style={{ width: `${qr.count > 0 ? (cnt / qr.count) * 100 : 0}%` }} /></div><span className="text-xs text-muted-foreground">{cnt}</span></div>
                      ))}</div>
                    ) : (
                      <div className="space-y-1 max-h-32 overflow-y-auto">{(qr.answers || []).map((a, i) => <p key={i} className="text-xs text-muted-foreground dark:text-muted-foreground p-1.5 bg-muted/50 dark:bg-muted rounded">{a}</p>)}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
